document.addEventListener('DOMContentLoaded', () => {
  // 1. DOM references
  const addToCartBtns = document.querySelectorAll('.is-add-to-cart-button');

  // 1b. Product JSON cache (by handle)
  const productJsonCache = new Map();

  const fetchProductJsonByHandle = async (handle) => {
    if (!handle) return null;
    if (productJsonCache.has(handle)) return productJsonCache.get(handle);

    const res = await fetch(`/products/${handle}.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return null;

    const data = await res.json().catch(() => null);
    productJsonCache.set(handle, data);
    return data;
  };

  // 1c. Read selected option values from the variant picker nearest the clicked button
  const getSelectedOptionValuesNearButton = (btn) => {
    const scope =
      btn.closest('.template_product_description_section') ||
      btn.closest('[li-section="Product Description"]') ||
      document;

    const variantSelects = scope.querySelector('variant-selects');
    if (!variantSelects) return null;

    // Collect in DOM order: selects and radio fieldsets (push checked radio per fieldset)
    const ordered = Array.from(
      variantSelects.querySelectorAll('select, fieldset input[type="radio"]')
    );

    const valuesInOrder = [];
    const seenFieldsets = new Set();

    ordered.forEach((el) => {
      if (el.tagName === 'SELECT') {
        valuesInOrder.push(el.value);
        return;
      }

      const fs = el.closest('fieldset');
      if (!fs || seenFieldsets.has(fs)) return;
      seenFieldsets.add(fs);

      const checked = fs.querySelector('input[type="radio"]:checked');
      if (checked) valuesInOrder.push(checked.value);
    });

    return valuesInOrder.length ? valuesInOrder : null;
  };

  // 1d. Match selections to variant id using product JSON variants
  const findVariantIdFromSelections = (productJson, optionValues) => {
    if (!productJson?.variants?.length || !optionValues?.length) return null;

    const match = productJson.variants.find((v) => {
      const vOpts = [v.option1, v.option2, v.option3].filter(Boolean);
      if (vOpts.length !== optionValues.length) return false;

      return vOpts.every((val, i) => String(val) === String(optionValues[i]));
    });

    return match ? match.id : null;
  };

  // 2. Dawn cart component resolver (drawer preferred, notification fallback)
  const getCartObject = () =>
    document.querySelector('cart-drawer') || document.querySelector('cart-notification');

  // 3. UI helpers: spinner controls
  const showButtonSpinner = (btn) => {
    const spinner = btn.querySelector('.loading__spinner');
    if (!spinner) return;
    spinner.classList.remove('hidden');
    spinner.setAttribute('aria-hidden', 'false');
  };

  const hideButtonSpinner = (btn) => {
    const spinner = btn.querySelector('.loading__spinner');
    if (!spinner) return;
    spinner.classList.add('hidden');
    spinner.setAttribute('aria-hidden', 'true');
  };

  // 4. UI helpers: button lock/unlock (prevents double click/tap)
  const lockButton = (btn) => {
    btn.dataset.locked = 'true';
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('tabindex', '-1'); // optional: prevent focus/keyboard activation
    btn.classList.add('is-disabled');
  };

  const unlockButton = (btn) => {
    delete btn.dataset.locked;
    btn.removeAttribute('aria-disabled');
    btn.removeAttribute('tabindex');
    btn.classList.remove('is-disabled');
  };

  // 5. Quantity resolver: read current PDP quantity input value
  const getQuantityForButton = (btn) => {
    // Prefer quantity input near this button (PDP-safe)
    const wrapper =
      btn.closest('.template_product_description_price_control') ||
      btn.parentElement ||
      document;

    const input =
      wrapper.querySelector('.pdp-qty__input') ||
      document.querySelector('.pdp-qty__input');

    const raw = input ? Number(input.value) : 1;

    // Safety fallback
    if (!Number.isFinite(raw) || raw < 1) return 1;
    return raw;
  };

  // 5b. Quantity input resolver: get the actual input element (near the button)
  const getQuantityInputForButton = (btn) => {
    const wrapper =
      btn.closest('.template_product_description_price_control') ||
      btn.parentElement ||
      document;

    return (
      wrapper.querySelector('.pdp-qty__input') ||
      document.querySelector('.pdp-qty__input')
    );
  };

  // 5c. Quantity reset: set input back to 1 (and notify any listeners)
  const resetQuantityForButton = (btn) => {
    const input = getQuantityInputForButton(btn);
    if (!input) return;

    input.value = 1;

    // If your plus/minus UI or other scripts listen to input changes, this helps keep them in sync
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  // 5d. Availability hint resolver (near the button)
  const getAvailabilityHintNearButton = (btn) => {
    const scope =
      btn.closest('.template_product_description_section') ||
      btn.closest('[li-section="Product Description"]') ||
      document;

    return scope.querySelector('.pdp-availability-hint');
  };

  // 5e. Inventory JSON resolver (reads <script type="application/json" data-variant-inventory>)
  const getVariantInventoryMapNearButton = (btn) => {
    const scope =
      btn.closest('.template_product_description_section') ||
      btn.closest('[li-section="Product Description"]') ||
      document;

    const script = scope.querySelector('script[type="application/json"][data-variant-inventory]');
    if (!script) return null;

    try {
      const arr = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(arr)) return null;

      const map = new Map();
      arr.forEach((row) => {
        if (!row) return;
        const id = row.id != null ? String(row.id) : null;
        if (!id) return;

        const qty = Number(row.inventory_quantity);
        map.set(id, Number.isFinite(qty) ? qty : 0);
      });

      return map;
    } catch {
      return null;
    }
  };

  // 5f. Availability hint UI helpers
  const showAvailabilityHint = (btn, message) => {
    const el = getAvailabilityHintNearButton(btn);
    if (!el) return;

    el.textContent = message || '';
    el.classList.remove('hidden');
  };

  const hideAvailabilityHint = (btn) => {
    const el = getAvailabilityHintNearButton(btn);
    if (!el) return;

    el.textContent = '';
    el.classList.add('hidden');
  };

  // 5g. Variant change watcher: hide availability hint + reset quantity when color/size changes
  const wireVariantChangeHideHint = () => {
    // Use event delegation so it works for swatches, pills, and dropdowns
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!target) return;

      const isVariantControl =
        target.matches('variant-selects select') ||
        target.matches('variant-selects input[type="radio"]') ||
        target.closest('variant-selects');

      if (!isVariantControl) return;

      const scope =
        target.closest('.template_product_description_section') ||
        target.closest('[li-section="Product Description"]') ||
        document;

      // Hide availability hint
      const hint = scope.querySelector('.pdp-availability-hint');
      if (hint) {
        hint.textContent = '';
        hint.classList.add('hidden');
      }

      // Reuse existing helper by resolving the related ATC button
      const btn = scope.querySelector('.is-add-to-cart-button');
      if (btn) {
        resetQuantityForButton(btn);
      }
    });
  };

  // init watcher
  wireVariantChangeHideHint();

  // 6. Core action: add to cart via /cart/add.js + request updated Dawn sections for UI rerender
  const addToCartWithSections = async (btn, quantity) => {
    // 6.1 Validate required button data
    if (!btn) throw new Error('No button found');

    // 6.2 Resolve variant id (prefer live selection, fallback to data-variant-id)
    let variantId = btn.getAttribute('data-variant-id');
    if (!variantId) throw new Error('Missing data-variant-id on button');

    const handle = btn.getAttribute('data-product-handle');
    const optionValues = getSelectedOptionValuesNearButton(btn);

    if (handle && optionValues) {
      const productJson = await fetchProductJsonByHandle(handle);
      const resolvedId = findVariantIdFromSelections(productJson, optionValues);
      if (resolvedId) variantId = String(resolvedId);
    }

    // 6.2b Inventory pre-check (only if the resolved variant id exists in the inventory JSON)
    const invMap = getVariantInventoryMapNearButton(btn);
    if (invMap && invMap.has(String(variantId))) {
      const available = invMap.get(String(variantId));

      // If the quantity we are trying to add exceeds the available inventory, show hint and stop
      if (Number.isFinite(available) && quantity > available) {
        showAvailabilityHint(
          btn,
          'The quantity you’re trying to add exceeds the available stock.'
        );
        throw new Error('Availability check failed');
      }
    }

    // 6.3 Gather request payload
    const cartObject = getCartObject();

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', String(quantity));

    // 6.4 Determine which sections to request (Dawn-style)
    if (cartObject && typeof cartObject.getSectionsToRender === 'function') {
      const sectionsToRender = cartObject
        .getSectionsToRender()
        .map((s) => s.section || s.id)
        .filter(Boolean);

      formData.append('sections', sectionsToRender.join(','));
      formData.append('sections_url', window.location.pathname + window.location.search);
    } else {
      // 6.4b Fallback section list (less reliable than getSectionsToRender)
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname + window.location.search);
    }

    // 6.5 Perform request
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.description || data?.message || 'Add to cart failed');
    }

    return data;
  };

  // 7. Cart drawer update: render returned sections + open drawer
  const openAndRenderDrawer = (state) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    // 7.1 Ensure drawer is in non-empty state before render/open
    drawer.classList.remove('is-empty');
    // 7.2 Render updated HTML fragments into drawer, then open
    if (typeof drawer.renderContents === 'function') drawer.renderContents(state);
    if (typeof drawer.open === 'function') drawer.open();
  };

  // 8. Wire up events: click handler for each custom ATC button
  addToCartBtns.forEach((btn) => {
    btn.addEventListener('click', async (event) => {
      // 8.1 Prevent default <a> navigation
      event.preventDefault();

      // 8.2 Guard against double click/tap
      if (btn.dataset.locked === 'true') return;
      lockButton(btn);

      try {
        // 8.3 Enter loading state (a11y + UI)
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add('is-loading');
        showButtonSpinner(btn);

        // 8.3b Clear previous availability hint on each attempt
        hideAvailabilityHint(btn);

        // 8.4 Resolve quantity from PDP input at click time
        const quantity = getQuantityForButton(btn);

        // 8.5 Add item, then update/open drawer
        const state = await addToCartWithSections(btn, quantity);
        openAndRenderDrawer(state);

        // 8.6 Reset quantity input after successful add
        resetQuantityForButton(btn);
      } catch (e) {
        // 8.6 Error handling
        console.warn(e?.message || e);
      } finally {
        // 8.7 Always reset UI state
        btn.removeAttribute('aria-busy');
        btn.classList.remove('is-loading');
        hideButtonSpinner(btn);
        unlockButton(btn);
      }
    });
  });
});
